import { Badge } from "@/ui";
import { Calendar, ArrowRight, User } from "lucide-react";
import { useLocation } from "wouter";
const blogsImage = "/assets/website/images/Healthcare_content_and_blogging_fc0318a8.png";

export default function Blogs() {
  const [, setLocation] = useLocation();

  const handleReadArticle = (slug: string) => {
    setLocation(`/blog/${slug}`);
  };

  return (
    <div
      className="min-h-screen font-sans"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      <main className="py-20">
        {/* Hero Image Section */}
        <div className="relative h-80 mb-20 overflow-hidden">
          <img 
            src={blogsImage} 
            alt="Healthcare Content and Blogging"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Badge variant="secondary" className="mb-4" data-testid="badge-blogs">
                Blog & Insights
              </Badge>
              <h1 className="text-3xl md:text-4xl font-normal text-primary mb-4" data-testid="text-blogs-title" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Healthcare AI Insights & Perspectives
              </h1>
              <p className="text-xl text-foreground max-w-3xl mx-auto" data-testid="text-blogs-description" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Explore the latest trends, research, and practical insights in healthcare AI, 
                clinical workflow optimization, and digital health innovation.
              </p>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto px-4">

          <div className="max-w-[1560px] mx-auto mb-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* CTO Blog - Agentic AI Needs a Finite State Machine (most recent) */}
            <article
              className="group cursor-pointer bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 border-l-4 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              style={{ borderLeftColor: "#1356a6" }}
              onClick={() => handleReadArticle('agentic-ai-fsm-not-vibes')}
              data-testid="card-blog-cto-fsm"
            >
              <div className="flex items-center gap-4 text-sm mb-4" style={{ color: "#64748b" }}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>May 16, 2026</span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Senthil Ramachandran</span>
                </div>
              </div>
              <h2
                className="text-3xl md:text-4xl font-normal mb-4 group-hover:text-primary transition-colors"
                style={{ fontFamily: "Montserrat, system-ui, sans-serif", color: "#1356a6" }}
              >
                Why Agentic AI Needs a Finite State Machine, Not Vibes
              </h2>
              <p className="text-sm mb-4" style={{ color: "#64748b" }}>
                Chief Technology Officer, AIscendus
              </p>
              <p className="text-lg leading-relaxed mb-6" style={{ color: "#475569" }}>
                The gap between agentic AI demos and shippable agentic AI isn't about model capability — it's about architecture. We learned the hard way building ContextEngine for healthcare: the model must run inside deterministic, reviewable rails, or it can't be shipped in any regulated domain.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge variant="outline" className="text-xs bg-white">Agentic AI</Badge>
                <Badge variant="outline" className="text-xs bg-white">Context Engineering</Badge>
                <Badge variant="outline" className="text-xs bg-white">Architecture</Badge>
                <Badge variant="outline" className="text-xs bg-white">Healthcare AI</Badge>
              </div>
              <div className="flex items-center gap-2 font-semibold group-hover:gap-3 transition-all" style={{ color: "#1356a6" }}>
                <span>Read Full Article</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </article>

            {/* CTO Blog - Why Rust for Healthcare */}
            <article
              className="group cursor-pointer bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 border-l-4 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              style={{ borderLeftColor: "#1356a6" }}
              onClick={() => handleReadArticle('why-rust-for-healthcare-agentic-ai')}
              data-testid="card-blog-cto-rust"
            >
              <div className="flex items-center gap-4 text-sm mb-4" style={{ color: "#64748b" }}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>March 21, 2026</span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Senthil Ramachandran</span>
                </div>
              </div>
              <h2
                className="text-3xl md:text-4xl font-normal mb-4 group-hover:text-primary transition-colors"
                style={{ fontFamily: "Montserrat, system-ui, sans-serif", color: "#1356a6" }}
              >
                Healthcare Agentic AI — Why We Chose Rust Over Python
              </h2>
              <p className="text-sm mb-4" style={{ color: "#64748b" }}>
                Chief Technology Officer, AIscendus
              </p>
              <p className="text-lg leading-relaxed mb-6" style={{ color: "#475569" }}>
                When we evaluated our technology choices through the lens of what healthcare actually demands — memory safety, type safety, real concurrency, and deterministic behavior — the answer wasn't Python. It was Rust.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge variant="outline" className="text-xs bg-white">Rust</Badge>
                <Badge variant="outline" className="text-xs bg-white">Agentic AI</Badge>
                <Badge variant="outline" className="text-xs bg-white">Healthcare Infrastructure</Badge>
                <Badge variant="outline" className="text-xs bg-white">Engineering</Badge>
              </div>
              <div className="flex items-center gap-2 font-semibold group-hover:gap-3 transition-all" style={{ color: "#1356a6" }}>
                <span>Read Full Article</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </article>

            {/* CTO Blog - From Reactive to Proactive */}
            <article 
              className="group cursor-pointer bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 border-l-4 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              style={{ borderLeftColor: "#1356a6" }}
              onClick={() => handleReadArticle('from-chart-to-claim-urgent-care')}
              data-testid="card-blog-cto"
            >
              <div className="flex items-center gap-4 text-sm mb-4" style={{ color: "#64748b" }}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>March 20, 2026</span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Senthil Ramachandran</span>
                </div>
              </div>
              <h2 
                className="text-3xl md:text-4xl font-normal mb-4 group-hover:text-primary transition-colors"
                style={{ fontFamily: "Montserrat, system-ui, sans-serif", color: "#1356a6" }}
              >
                From Reactive to Proactive: How AIscendus Stops Denials Before They Start
              </h2>
              <p className="text-sm mb-4" style={{ color: "#64748b" }}>
                Chief Technology Officer, AIscendus
              </p>
              <p className="text-lg leading-relaxed mb-6" style={{ color: "#475569" }}>
                Urgent care bleeds revenue through preventable denials — 12% denial rates with 84% potentially avoidable. AIscendus breaks the reactive cycle by working alongside your team as an expert partner at every stage, engineering claims for payment from the moment a patient walks in.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge variant="outline" className="text-xs bg-white">Revenue Cycle Management</Badge>
                <Badge variant="outline" className="text-xs bg-white">Agentic AI</Badge>
                <Badge variant="outline" className="text-xs bg-white">Urgent Care</Badge>
                <Badge variant="outline" className="text-xs bg-white">RCM</Badge>
              </div>
              <div className="flex items-center gap-2 font-semibold group-hover:gap-3 transition-all" style={{ color: "#1356a6" }}>
                <span>Read Full Article</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </article>

            {/* CISO Blog - UCA Continuous Compliance */}
            <article
              className="group cursor-pointer bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 border-l-4 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              style={{ borderLeftColor: "#1356a6" }}
              onClick={() => handleReadArticle('uca-continuous-compliance')}
              data-testid="card-blog-ciso-compliance"
            >
              <div className="flex items-center gap-4 text-sm mb-4" style={{ color: "#64748b" }}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>March 21, 2026</span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Nicole Cella</span>
                </div>
              </div>
              <h2
                className="text-3xl md:text-4xl font-normal mb-4 group-hover:text-primary transition-colors"
                style={{ fontFamily: "Montserrat, system-ui, sans-serif", color: "#1356a6" }}
              >
                UCA: The Rise of AI-Driven Continuous Compliance
              </h2>
              <p className="text-sm mb-4" style={{ color: "#64748b" }}>
                Chief Information Security Officer, Aiscendus
              </p>
              <p className="text-lg leading-relaxed mb-6" style={{ color: "#475569" }}>
                Urgent care compliance has outgrown the binder. With evolving HIPAA rules, the reality of evidence decay, and UCA accreditation demands, the question is no longer whether you passed your last audit, it's whether you could pass one right now.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge variant="outline" className="text-xs bg-white">Compliance</Badge>
                <Badge variant="outline" className="text-xs bg-white">HIPAA</Badge>
                <Badge variant="outline" className="text-xs bg-white">Urgent Care</Badge>
                <Badge variant="outline" className="text-xs bg-white">Continuous Assurance</Badge>
              </div>
              <div className="flex items-center gap-2 font-semibold group-hover:gap-3 transition-all" style={{ color: "#1356a6" }}>
                <span>Read Full Article</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </article>

            {/* CISO Blog - Building Customer Trust in AI Platforms */}
            <article
              className="group cursor-pointer bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 border-l-4 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              style={{ borderLeftColor: "#1356a6" }}
              onClick={() => handleReadArticle('building-customer-trust-ai-platforms')}
              data-testid="card-blog-ciso"
            >
              <div className="flex items-center gap-4 text-sm mb-4" style={{ color: "#64748b" }}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>February 13, 2026</span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Nicole Cella</span>
                </div>
              </div>
              <h2 
                className="text-3xl md:text-4xl font-normal mb-4 group-hover:text-primary transition-colors"
                style={{ fontFamily: "Montserrat, system-ui, sans-serif", color: "#1356a6" }}
              >
                Building Customer Trust in AI Platforms: How Secure, Intelligent Automation Powers Confident Healthcare Decisions
              </h2>
              <p className="text-sm mb-4" style={{ color: "#64748b" }}>
                Chief Information Security Officer, Aiscendus
              </p>
              <p className="text-lg leading-relaxed mb-6" style={{ color: "#475569" }}>
                From a CISO's perspective, customer trust in AI platforms is built through deliberate design choices and consistent execution—aligning security, privacy, and compliance with how AI-powered automation functions in real-world healthcare environments.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge variant="outline" className="text-xs bg-white">Security</Badge>
                <Badge variant="outline" className="text-xs bg-white">Trust</Badge>
                <Badge variant="outline" className="text-xs bg-white">Healthcare AI</Badge>
                <Badge variant="outline" className="text-xs bg-white">Governance</Badge>
              </div>
              <div className="flex items-center gap-2 font-semibold group-hover:gap-3 transition-all" style={{ color: "#1356a6" }}>
                <span>Read Full Article</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </article>

            {/* CIO Blog - Improving Patient-Centric, Value-Based Care */}
            <article 
              className="group cursor-pointer bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 border-l-4 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              style={{ borderLeftColor: "#1356a6" }}
              onClick={() => handleReadArticle('improving-patient-centric-value-based-care')}
              data-testid="card-blog-2"
            >
              <div className="flex items-center gap-4 text-sm mb-4" style={{ color: "#64748b" }}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>November 15, 2025</span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>E. Llewellyn Weeks</span>
                </div>
              </div>
              <h2 
                className="text-3xl md:text-4xl font-normal mb-4 group-hover:text-primary transition-colors"
                style={{ fontFamily: "Montserrat, system-ui, sans-serif", color: "#1356a6" }}
              >
                Improving Patient-Centric, Value-Based Care through Emerging AI Technologies
              </h2>
              <p className="text-sm mb-4" style={{ color: "#64748b" }}>
                Chief Information Officer, Aiscendus
              </p>
              <p className="text-lg leading-relaxed mb-6" style={{ color: "#475569" }}>
                Exploring how Agentic AI transforms healthcare delivery through intelligent, adaptive systems that balance technology and humanity, empowering both clinicians and patients for better outcomes.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge variant="outline" className="text-xs bg-white">Agentic AI</Badge>
                <Badge variant="outline" className="text-xs bg-white">Value-Based Care</Badge>
                <Badge variant="outline" className="text-xs bg-white">Patient-Centric</Badge>
              </div>
              <div className="flex items-center gap-2 font-semibold group-hover:gap-3 transition-all" style={{ color: "#1356a6" }}>
                <span>Read Full Article</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </article>
          </div>
        </div>
      </main>
    </div>
  );
}